import React from 'react'
import {Box, Link, LinkProps, Text, Image} from '@miro-site/core'
import styled, {css, useTheme} from 'styled-components'

const Container = styled(Box).attrs(({theme: {miroverseHighlighted}}) => ({
  $py: miroverseHighlighted.container.verticalPadding,
  $px: miroverseHighlighted.container.horizontalPadding,
  $styles: {
    borderRadius: miroverseHighlighted.container.radius,
    backgroundColor: miroverseHighlighted.container.background,
  },
}))``

const MiroverseTitle = styled(Text).attrs(({theme: {miroverseHighlighted}}) => ({
  as: miroverseHighlighted.title.as,
  $variant: miroverseHighlighted.title.variant,
  $styles: {
    fontWeight: miroverseHighlighted.title.fontWeightV1,
    color: miroverseHighlighted.title.colorV1,
  },
}))({})

const MiroverseTitleV2 = styled(Text).attrs(({theme: {miroverseHighlighted}}) => ({
  as: miroverseHighlighted.title.as,
  $variant: miroverseHighlighted.title.variant,
  $styles: {
    fontWeight: miroverseHighlighted.title.fontWeightV2,
    color: miroverseHighlighted.title.colorV2,
    marginBottom: miroverseHighlighted.title.margins.afterTitle,
  },
}))({})

const ItemContainer = styled(Box).attrs(({theme: {miroverseHighlighted}}) => ({
  $styles: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingBottom: miroverseHighlighted.itemContainer.verticalPadding,
    paddingTop: miroverseHighlighted.itemContainer.verticalPadding,
  },
}))``

export const ItemLink = styled(Link)`
  display: block;
  &:focus {
    ${props => props.theme.normalizeStyles(props.theme.focus.outlines.withOffset)}
  }
`

type HeroBannerLinkProps = LinkProps & {lineBottom?: boolean}

const HeroBannerLink = styled(ItemLink)<HeroBannerLinkProps>`
  ${({theme: {miroverseHighlighted}, lineBottom = false}) =>
    lineBottom &&
    css`
      border-bottom: 1px solid ${miroverseHighlighted.itemContainer.link.dividerColor};
    `}
`

const ActionLink = styled(Link).attrs(({theme: {miroverseHighlighted}}) => ({
  $variant: miroverseHighlighted.actionLink.variant,
  $color: miroverseHighlighted.actionLink.color,
  $size: miroverseHighlighted.actionLink.size,
  withArrow: miroverseHighlighted.actionLink.withArrow,
}))`
  ${({theme: {normalizeStyles, miroverseHighlighted}}) =>
    normalizeStyles({
      fontSize: miroverseHighlighted.actionLink.fontSize,
      fontWeight: miroverseHighlighted.actionLink.fontWeight,
    })}
`

const ItemTitle = styled(Text).attrs(({theme: {miroverseHighlighted}}) => ({
  $styles: {
    fontSize: miroverseHighlighted.itemContainer.itemTitle.fontSize,
    lineHeight: miroverseHighlighted.itemContainer.itemTitle.lineHeight,
    color: miroverseHighlighted.itemContainer.itemTitle.color,
  },
}))``

export const ItemDescription = styled(Text).attrs(({theme: {miroverseHighlighted}}) => ({
  $styles: {
    fontSize: miroverseHighlighted.itemContainer.itemDescription.fontSize,
    lineHeight: miroverseHighlighted.itemContainer.itemDescription.lineHeight,
    color: miroverseHighlighted.itemContainer.itemDescription.color,
  },
}))``
export const CategoryHeroBannerV1 = (): React.ReactElement => {
  const {miroverseHighlighted} = useTheme()
  return (
    <Container data-track-place="Hero Category Banner">
      <MiroverseTitle>
        Templates from <br /> our community
      </MiroverseTitle>
      <HeroBannerLink target="_blank" href={'/miroverse/'}>
        <ItemContainer>
          <Box $styles={{width: miroverseHighlighted.itemContainer.imageWidthV1}}>
            <Image
              name="miroverse-profiles"
              aria-hidden={true}
              src={
                'https://images.ctfassets.net/qop92tnevinq/6YEjAd5p8dQnQAFqjx3HAV/d6cc387f649b467cf7c37c5c230c143e/miroverse_banner_faces_small.png'
              }
            />
          </Box>
        </ItemContainer>
      </HeroBannerLink>
      <ActionLink target="_blank" href={'/miroverse/'}>
        Explore
      </ActionLink>
    </Container>
  )
}

interface BannerItem {
  slug: string
  image: string
  templateName: string
  authorName: string
}

const bannerItems: BannerItem[] = [
  {
    slug: 'effective-meetings',
    image:
      'https://images.ctfassets.net/qop92tnevinq/5po8ME6Qwa3gLVE1AQpvtB/dd43f41540dd6ec293743706ac80a158/website_logo_zoom.png',
    templateName: 'Effective Meetings',
    authorName: 'Zoom',
  },
  {
    slug: 'design-sprint-jake-knapp',
    image:
      'https://images.ctfassets.net/qop92tnevinq/1T3F2z5K3V3nxeJfzJHFLn/b676070f4202b95e6144ab8656adc74f/website_logo_jake_3x.png',
    templateName: 'Design sprint',
    authorName: 'Jake Knapp',
  },
  {
    slug: 'the-way-we-work',
    image:
      'https://images.ctfassets.net/qop92tnevinq/20MjsJ5KU31KxMjwJxw9dJ/769903084cda87f09131e3dc1aca3b37/website_logo_deloitte.png',
    templateName: 'The Way We Work',
    authorName: 'Deloitte',
  },
  {
    slug: 'emotions-wheel-icebreaker',
    image:
      'https://images.ctfassets.net/qop92tnevinq/4n08JewmClIro3btheyYOl/9196ba54ebfa7943286714c4ba577fc5/website_logo_anya.png',
    templateName: 'Emotions Wheel Icebreaker',
    authorName: 'Anya Dvornikova',
  },
]

export const CategoryHeroBannerV2 = (): React.ReactElement => {
  const {miroverseHighlighted} = useTheme()
  return (
    <Container data-track-place="Hero Category Banner">
      <MiroverseTitleV2>Featured templates</MiroverseTitleV2>
      <ul>
        {bannerItems.map((item, idx) => {
          return (
            <li key={item.slug}>
              <HeroBannerLink
                lineBottom={idx !== bannerItems.length - 1}
                target="_blank"
                href={`/miroverse/${item.slug}/`}
              >
                <ItemContainer>
                  <Box
                    $mr={miroverseHighlighted.itemContainer.margins.afterImage}
                    $styles={{width: miroverseHighlighted.itemContainer.imageWidthV2}}
                  >
                    <Image name="miroverse-profiles" aria-hidden={true} src={item.image} />
                  </Box>
                  <Box $styles={{width: miroverseHighlighted.itemContainer.textWidthV2}}>
                    <ItemTitle>{item.templateName}</ItemTitle>
                    <ItemDescription>by {item.authorName}</ItemDescription>
                  </Box>
                </ItemContainer>
              </HeroBannerLink>
            </li>
          )
        })}
      </ul>
      <ActionLink
        $styles={{marginTop: miroverseHighlighted.actionLink.margins.beforeLink}}
        target="_blank"
        href={'/miroverse/'}
      >
        More in Miroverse
      </ActionLink>
    </Container>
  )
}
